import { useMemo, useState } from 'react';
import {
  CONSUMER_CARE_SETTINGS,
  DEFAULT_CONSUMER_CARE_SETTING,
  careAdjustmentsOf,
  isCustomCare,
} from '@shared/consumerPlan';
import { CareScenarioCard } from '../components/care-scenario-card';
import { ChoiceList } from '../components/choice-list';
import { StepFrame } from '../components/step-frame';
import { useCarePlanEditor } from '../hooks/use-care-plan-editor';
import { useConsumerClient } from '../hooks/use-consumer-client';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { CARE_SETTING_COPY } from '../lib/copy';
import type { ConsumerStepProps } from './step-props';

export function CareSettingStep({ clientId }: ConsumerStepProps) {
  const { finish } = useConsumerFlow(clientId);
  const { client, scenario } = useConsumerClient(clientId);
  const { previewScenario, saveScenario } = useCarePlanEditor(client);
  const [setting, setSetting] = useState<ConsumerCareSetting>(
    scenario?.careSetting ?? DEFAULT_CONSUMER_CARE_SETTING,
  );
  const preview = useMemo(() => previewScenario({ careSetting: setting }), [previewScenario, setting]);

  const handleUse = () => {
    if (setting !== scenario?.careSetting) saveScenario({ careSetting: setting });
    finish('scenario');
  };

  return (
    <StepFrame
      step="care-setting"
      title="Where would you like to receive care?"
      subtitle="Choose the setting to plan for. We’ll update your care scenario to match."
      onBack={() => finish('scenario')}
      primary={{ label: 'Use this setting', onClick: handleUse }}
      next="return to your care scenario."
    >
      <ChoiceList
        label="Where care happens"
        value={setting}
        onChange={setSetting}
        choices={CONSUMER_CARE_SETTINGS.map(value => ({ value, ...CARE_SETTING_COPY[value] }))}
      />
      {preview && client && (
        <CareScenarioCard scenario={preview} isCustom={isCustomCare(careAdjustmentsOf(client))} />
      )}
    </StepFrame>
  );
}
